import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useAccountHistorySync } from '../../hooks/useAccountHistorySync';
import { useAlphalendEventSubscription } from '../../hooks/useAlphalendEventSubscription';
import { PieChart, formatValue, convertToKAndM } from 'ui-kit';
import { marketsArray } from '../../data/marketInfo';
import { getTokenIconUrl, getColorBySymbol, getTokenConfigBySymbol } from '../../utils/tokenUtils';
import SyncStatusIndicator from './SyncStatusIndicator';
import { formatDynamicCurrency } from '../../utils/formatUtils';
const BorrowAllocation = ({ positionCapId, portfolioBorrows, // NO default value - null/undefined means "loading"
 }) => {
    // Sync progress for status indicator (visual feedback only)
    // The pie chart data comes from portfolio, not sync service
    const syncProgress = useAccountHistorySync(positionCapId);
    // Market data for latest prices (more accurate than portfolio snapshot)
    const { data: marketHighFreqData } = useAlphalendEventSubscription('market_data', 'market_data_high_freq');
    const marketsLoading = !marketHighFreqData;
    // Determine if portfolio data is still loading
    const isPortfolioLoading = portfolioBorrows === undefined || portfolioBorrows === null;
    // Full loading state: portfolio loading OR markets loading
    // We need markets for accurate USD calculations
    const isLoading = isPortfolioLoading || marketsLoading;
    // Create price map from market data (more accurate than portfolio prices)
    const tokenPrices = useMemo(() => {
        if (!marketHighFreqData)
            return {};
        const prices = {};
        Object.entries(marketHighFreqData.markets).forEach(([marketId, marketData]) => {
            const marketDetails = marketsArray.find((m) => m.id === marketId);
            if ((marketDetails === null || marketDetails === void 0 ? void 0 : marketDetails.symbol) && marketData.price) {
                const symbol = marketDetails.symbol.toUpperCase();
                prices[symbol] = parseFloat(marketData.price);
            }
        });
        return prices;
    }, [marketHighFreqData]);
    // Build chart data from portfolio (immediately available)
    // This replaces the old sync-dependent data fetching
    const chartData = useMemo(() => {
        // During loading or no data, return empty array
        // PieChart component handles skeleton display via isLoading prop
        if (isPortfolioLoading || !portfolioBorrows || portfolioBorrows.length === 0) {
            return [];
        }
        // Calculate USD values using best available prices
        const itemsWithUSD = portfolioBorrows.map((token) => {
            const tokenConfig = getTokenConfigBySymbol(token.symbol);
            // Prefer live market price, fallback to portfolio snapshot price
            const currentPrice = tokenPrices[tokenConfig.symbol.toUpperCase()] || token.currentPrice;
            const valueInUSD = token.currentDebt * currentPrice;
            return {
                symbol: token.symbol,
                icon: token.icon || getTokenIconUrl(token.symbol),
                valueInUSD,
                currentDebt: token.currentDebt,
            };
        });
        // Calculate total for percentage calculations
        const totalBorrowed = itemsWithUSD.reduce((sum, item) => sum + item.valueInUSD, 0);
        // Transform into chart-compatible format
        return itemsWithUSD
            .map((item) => {
            const percentage = totalBorrowed > 0 ? (item.valueInUSD / totalBorrowed) * 100 : 0;
            // Format amount for display
            const formattedAmount = item.valueInUSD < 0.001
                ? '< $0.001'
                : item.valueInUSD >= 0.01
                    ? formatValue(item.valueInUSD)
                    : '$' + convertToKAndM(item.valueInUSD, { decimals: 4 });
            // Format percentage for legend
            const formattedPercentage = percentage < 0.1 ? '< 0.1%' : percentage.toFixed(percentage < 1 ? 2 : 1) + '%';
            return {
                name: item.symbol,
                value: percentage,
                amount: formattedAmount,
                displayPercentage: formattedPercentage,
                rawValue: item.valueInUSD,
                color: getColorBySymbol(item.symbol),
                icon: item.icon,
            };
        })
            .filter((item) => item.value > 0)
            .sort((a, b) => b.rawValue - a.rawValue);
    }, [portfolioBorrows, tokenPrices, isPortfolioLoading]);
    // Total debt from chart data (for badge display)
    const totalBorrowedValue = useMemo(() => {
        return chartData.reduce((sum, item) => sum + (item.rawValue || 0), 0);
    }, [chartData]);
    // Truly has no borrows: NOT loading AND array is empty
    const hasNoBorrows = !isPortfolioLoading && (portfolioBorrows === null || portfolioBorrows === void 0 ? void 0 : portfolioBorrows.length) === 0;
    // Hide component completely if there are no borrows (after loading complete)
    // This matches the original behavior - borrow section only shows if user has borrows
    if (hasNoBorrows) {
        return null;
    }
    // Also hide if total value is effectively 0 (e.g., all debts repaid but records exist)
    if (!isLoading && totalBorrowedValue === 0 && portfolioBorrows && portfolioBorrows.length > 0) {
        return null;
    }
    return (_jsxs("div", { className: `theme-bg-lndPrimary sm:rounded-3xl rounded-xl lg:!p-12 px-5 py-6 shadow-sm`, children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "lg:text-2xl text-base font-InterSemiBold theme-text-lndPrimary", children: "Borrow Details" }), !isLoading && _jsx(SyncStatusIndicator, { syncProgress: syncProgress })] }), isLoading ? (_jsx(DebtBadgeSkeleton, {})) : totalBorrowedValue > 0 ? (_jsxs("div", { className: "flex flex-row flex-nowrap items-center lg:gap-2 gap-1 theme-bg-lndTableInfo lg:rounded-xl rounded-md lg:px-4 px-2 lg:py-2 py-1", children: [_jsx("div", { className: "lg:text-sm text-xs theme-text-lndSecondary", children: "Debt" }), _jsxs("div", { className: "lg:text-base text-xs font-InterSemiBold theme-text-lndPrimary", children: ["$", formatDynamicCurrency(totalBorrowedValue)] })] })) : null] }), _jsx("div", { className: "flex w-full py-5", children: _jsx(PieChart, { data: chartData, isLoading: isLoading, showLegend: true, showTooltip: true, emptyStateText: "You have no borrows" // Only shows when !isLoading && data.length === 0
                 }) })] }));
};
// Skeleton for the debt badge during loading
const DebtBadgeSkeleton = () => (_jsx("div", { className: "w-[86px] h-[24px] lg:w-[124px] lg:h-[40px] theme-bg-lndTableInfo lg:rounded-xl rounded-md animate-pulse" }));
export default React.memo(BorrowAllocation);
